
import React from "react";
import { Table, Tooltip, Tag, Dropdown, MenuProps, Progress } from "antd";
import type { ColumnsType } from "antd/es/table";

import type { Application, ApplicationFilters } from "../../../../core/data/redux/recruitment/types";
import { STATUS_COLORS } from "../../constants/recruitment.constants";
import { humanReadableDate } from "../../../../helper/dateTimeHelper";

import ApplicationPagination from "./ApplicationPagination";
import StatusCell from "./StatusCell";

interface ApplicationTableProps {
  applications: Application[];
  loading: boolean;
  filters: ApplicationFilters;
  totalCount: number;
  selectedRowKeys: React.Key[];
  onFilterChange: (partial: Partial<ApplicationFilters>) => void;
  onSelectionChange: (keys: React.Key[]) => void;
  onViewDetails: (id: number) => void;
  onUpdateStatus: (app: Application, defaultStatus?: string) => void;
  onBulkMoveStage: (targetStatus: string) => void;
  onBulkReject: () => void;
  onExport: () => void;
  onScheduleInterview?: (app: Application) => void;
}


const hexToAntColor = (hex: string) => {
  const map: Record<string, string> = {
    "#FFA500": "orange",
    "#3498DB": "blue",
    "#2ECC71": "green",
    "#9B59B6": "purple",
    "#F39C12": "gold",
    "#27AE60": "green",
    "#16A085": "cyan",
    "#E74C3C": "red",
    "#00C851": "green",
    "#95A5A6": "default",
    "#E67E22": "orange",
  };
  return map[hex] || "default";
};

const getAntColor = (status: string) => hexToAntColor(STATUS_COLORS[status] || "#95A5A6");


const Avatar: React.FC<{ name: string; url?: string }> = ({ name, url }) => {
  const [error, setError] = React.useState(false);
  
  if (url && !error) {
    return (
      <img
        src={url}
        alt={name}
        className="rounded-circle flex-shrink-0"
        style={{ width: 34, height: 34, objectFit: "cover" }}
        onError={() => setError(true)}
      />
    );
  }

  return (
    <div
      className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
      style={{
        width: 34,
        height: 34,
        background: "linear-gradient(135deg, #0d6efd, #6610f2)",
        fontSize: 12,
        flexShrink: 0,
      }}
    >
      {(name || "?")
        .split(" ")
        .map((n) => n[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()}
    </div>
  );
};

const ApplicationTable: React.FC<ApplicationTableProps> = ({
  applications,
  loading,
  filters,
  totalCount,
  selectedRowKeys,
  onFilterChange,
  onSelectionChange,
  onViewDetails,
  onUpdateStatus,
  onBulkMoveStage,
  onBulkReject,
  onExport,
  onScheduleInterview,
}) => {
  const getActionItems = (app: Application): MenuProps["items"] => [
    {
      key: "view",
      icon: <i className="ti ti-eye" style={{ color: "#3b82f6" }} />,
      label: "View Details",
      onClick: (info) => {
        info.domEvent.stopPropagation();
        onViewDetails(app.id);
      },
    },
    {
      key: "schedule",
      icon: <i className="ti ti-calendar-event" style={{ color: "#10b981" }} />,
      label: "Schedule Interview",
      onClick: (info) => {
        info.domEvent.stopPropagation();
        onScheduleInterview?.(app);
      },
    },
    { type: "divider" },
    {
      key: "reject",
      icon: <i className="ti ti-user-x" />,
      danger: true,
      label: "Reject",
      onClick: (info) => {
        info.domEvent.stopPropagation();
        onUpdateStatus(app, "rejected");
      },
    },
  ];

  const columns: ColumnsType<Application> = [
    {
      title: "SrNo.",
      dataIndex: "srNo",
      key: "srNo",
      width: 70,
      align: "center",
      render: (_, __, index) => (
        <span style={{ fontSize: 12, fontWeight: 500, color: "#6b7280" }}>
          {((filters.page || 1) - 1) * (filters.limit || 10) + index + 1}
        </span>
      ),
    },
    {
      title: <Tooltip title="Candidate name & contact">Candidate</Tooltip>,
      key: "candidate",
      width: 240,
      sorter: (a, b) =>
        (a.candidate?.full_name || "").localeCompare(b.candidate?.full_name || ""),
      render: (_, record) => (
        <Tooltip
          title={
            <div className="p-1">
              <div className="fw-bold">{record.candidate?.full_name}</div>
              <div className="fs-12">{record.candidate?.email}</div>
              <div className="fs-12">{record.candidate?.phone}</div>
              {record.candidate?.current_company && (
                <div className="fs-12 text-muted">
                  <i className="ti ti-building me-1" />
                  {record.candidate.current_company}
                </div>
              )}
              {record.candidate?.total_experience_years != null && (
                <div className="fs-12 text-muted">
                  {record.candidate.total_experience_years} yrs exp
                </div>
              )}
            </div>
          }
          color="#212529"
          placement="right"
        >
          <div
            className="d-flex align-items-center gap-2"
            style={{ cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails(record.id);
            }}
          >
            <Avatar name={record.candidate?.full_name || "?"} url={(record.candidate as any)?.profile_pic_serve_url} />
            <div>
              <h6 className="fw-medium fs-14 mb-0 text-truncate" style={{ maxWidth: 175 }}>
                {record.candidate?.full_name}
              </h6>
              <span className="text-muted fs-12">{record.candidate?.email}</span>
            </div>
          </div>
        </Tooltip>
      ),
    },
    {
      title: <Tooltip title="Applied job title & location">Job</Tooltip>,
      key: "job",
      width: 190,
      render: (_, record) => (
        <Tooltip
          title={
            <div>
              <div className="fw-bold">{record.job?.job_title}</div>
              <div className="fs-12">
                <i className="ti ti-map-pin me-1" />
                {record.job?.work_location}
              </div>
              {record.job?.job_code && (
                <div className="fs-12 text-muted">#{record.job.job_code}</div>
              )}
            </div>
          }
          color="#1e293b"
          placement="right"
        >
          <div className="d-flex flex-column">
            <span className="fw-medium text-truncate" style={{ maxWidth: 170, fontSize: 13 }}>
              {record.job?.job_title}
            </span>
            <span className="text-muted text-truncate" style={{ fontSize: 11.5, maxWidth: 170 }}>
              <i className="ti ti-map-pin me-1" />
              {record.job?.work_location}
            </span>
          </div>
        </Tooltip>
      ),
    },
    {
      title: <Tooltip title="ATS Match Score">Score</Tooltip>,
      dataIndex: "ats_score",
      key: "ats_score",
      width: 90,
      align: "center",
      sorter: (a, b) => parseFloat(String(a.ats_score || 0)) - parseFloat(String(b.ats_score || 0)),
      render: (score) => {
        const num = score !== null ? parseFloat(String(score)) : null;
        if (num === null) return <span className="text-muted fs-12">N/A</span>;
        return (
          <Tooltip title={`${num.toFixed(0)}% ATS Match`}>
            <Progress
              type="circle"
              percent={Math.round(num)}
              size={36}
              strokeColor={num >= 80 ? "#28a745" : num >= 60 ? "#ffc107" : "#dc3545"}
            />
          </Tooltip>
        );
      },
    },
    {
      title: <Tooltip title="Application source channel">Source</Tooltip>,
      dataIndex: "source",
      key: "source",
      width: 115,
      render: (source: string) => (
        <Tag color="cyan" style={{ fontSize: 11.5 }}>
          {(source || "").replace(/_/g, " ")}
        </Tag>
      ),
    },
    {
      title: <Tooltip title="Priority level">Priority</Tooltip>,
      dataIndex: "priority",
      key: "priority",
      width: 100,
      render: (priority: string | null) => {
        if (!priority) return <span className="text-muted fs-12">—</span>;
        const colorMap: Record<string, string> = {
          urgent: "red",
          high: "orange",
          normal: "blue",
          low: "default",
        };
        return (
          <Tag color={colorMap[priority] || "default"} style={{ fontSize: 11.5 }}>
            {priority}
          </Tag>
        );
      },
    },
    {
      title: <Tooltip title="Date of application">Applied</Tooltip>,
      dataIndex: "applied_at",
      key: "applied_at",
      width: 110,
      sorter: (a, b) =>
        new Date(a.applied_at).getTime() - new Date(b.applied_at).getTime(),
      render: (date) => (
        <Tooltip title={humanReadableDate(date)}>
          <span className="text-muted" style={{ fontSize: 12.5 }}>
            <i className="ti ti-calendar me-1" />
            {humanReadableDate(date)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (_: any, record: Application) => (
        <StatusCell application={record} onEdit={onUpdateStatus} />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => (
        <Dropdown
          menu={{ items: getActionItems(record) }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <button
            className="btn btn-icon btn-sm btn-light"
            style={{ borderRadius: "6px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <i className="ti ti-dots-vertical" />
          </button>
        </Dropdown>
      ),
    },
  ];

  const hasSelection = selectedRowKeys.length > 0;

  return (
    <div className="card-body p-0 mb-3 border-0 slide-up-enter">
      
      {hasSelection && (
        <div
          className="d-flex align-items-center justify-content-between flex-wrap px-4 py-2 bg-white border-bottom"
          style={{ gap: 8 }}
        >
          <span className="fw-medium text-primary d-flex align-items-center">
            <i className="ti ti-checks me-2" />
            {selectedRowKeys.length} candidate{selectedRowKeys.length > 1 ? "s" : ""} selected
          </span>
          <div className="d-flex gap-2 flex-wrap">
            <button
              className="btn btn-sm btn-soft-primary"
              onClick={() => onBulkMoveStage("shortlisted")}
            >
              <i className="ti ti-arrow-up-right me-1" /> Shortlist
            </button>
            <button
              className="btn btn-sm btn-soft-warning"
              onClick={() => onBulkMoveStage("screening")}
            >
              <i className="ti ti-filter me-1" /> Screening
            </button>
            <button
              className="btn btn-sm btn-soft-info"
              onClick={onExport}
            >
              <i className="ti ti-file-export me-1" /> Export
            </button>
            <button
              className="btn btn-sm btn-soft-danger"
              onClick={onBulkReject}
            >
              <i className="ti ti-user-x me-1" /> Reject All
            </button>
          </div>
        </div>
      )}

      
      {loading ? (
        <div
          className="d-flex justify-content-center align-items-center bg-white"
          style={{ minHeight: 400, borderRadius: 12 }}
        >
          <div className="text-center">
            <div
              className="spinner-border text-primary"
              role="status"
              style={{ width: "3rem", height: "3rem" }}
            />
            <p className="mt-3 text-muted">Loading applications...</p>
          </div>
        </div>
      ) : applications.length > 0 ? (
        <div className="bg-white" style={{ borderRadius: 12, overflow: "hidden", border: "1px solid #e2e8f0" }}>
          <Table
            className="custom-data-table"
            columns={columns}
            dataSource={applications}
            rowKey="id"
            size="middle"
            pagination={false}
            scroll={{ x: 1200 }}
            
            
            
            
            
            rowClassName={(_, index) =>
              index % 2 === 0 ? "data-table-row-light" : "data-table-row-dark"
            }
          
          
          
          
          />
          <ApplicationPagination
            currentPage={filters.page || 1}
            totalPages={Math.ceil(totalCount / (filters.limit || 10))}
            total={totalCount}
            limit={filters.limit || 10}
            hasNextPage={(filters.page || 1) * (filters.limit || 10) < totalCount}
            hasPrevPage={(filters.page || 1) > 1}
            onPageChange={(page) => onFilterChange({ page })}
            onLimitChange={(limit) => onFilterChange({ limit, page: 1 })}
          />
        </div>
      ) : (
        <div
          className="d-flex justify-content-center align-items-center bg-white"
          style={{ minHeight: 400, borderRadius: 12, border: "1px solid #e2e8f0" }}
        >
          <div className="text-center">
            <i className="ti ti-inbox text-muted" style={{ fontSize: "4rem" }} />
            <h5 className="mt-3 mb-2 fw-semibold">No Applications Found</h5>
            <p className="text-muted mb-0">Try adjusting your filters or check back later.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationTable;
